import { createContext, ReactNode, FC, useState, useContext } from 'react';
import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
} from '@chakra-ui/react';
import { When } from 'react-if';

export interface ModalObj {
  title?: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  closeable?: boolean;
  modalProps?: Partial<ModalProps>;
}

export interface ModalState {
  isOpen: boolean;
  modal: ModalObj;
  openModal: (modal: ModalObj) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalState>({
  isOpen: false,
  modal: {
    title: null,
    body: null,
    footer: null,
    closeable: true,
    modalProps: {},
  },
  openModal: () => undefined,
  closeModal: () => undefined,
});

export const ModalProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<ModalObj>({
    title: null,
    body: null,
    footer: null,
    closeable: true,
    modalProps: {},
  });

  return (
    <ModalContext.Provider
      value={{
        isOpen: open,
        modal,
        openModal: (modal) => {
          setModal(modal);
          setOpen(true);
        },
        closeModal: () => {
          setOpen(false);
        },
      }}
    >
      {children}

      <Modal
        onClose={() => (modal.closeable ? setOpen(false) : undefined)}
        isOpen={open}
        isCentered
        {...modal.modalProps}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modal.title}</ModalHeader>
          <When condition={modal.closeable}>
            <ModalCloseButton />
          </When>
          <ModalBody>{modal.body}</ModalBody>
          <ModalFooter>
            <HStack spacing={4}>{modal.footer}</HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ModalContext.Provider>
  );
};

/** Wrap your component with withModal HOC before use this hook! */
export const useModal = () => useContext(ModalContext);

export const withModal = (Component: FC<any>): FC => {
  const HOCModal = (props) => (
    <ModalProvider>
      <Component {...props} />
    </ModalProvider>
  );
  return HOCModal;
};

export default ModalContext;
